import { Brain } from "lucide-react";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

export const RecentApplications = () => {
  const { data: applications, isLoading, error } = useQuery({
    queryKey: ['recentApplications'],
    queryFn: async () => {
      // First get the lender ID for the current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { data: lender, error: lenderError } = await supabase
        .from('lenders')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (lenderError) throw lenderError;
      if (!lender) throw new Error('No lender found');

      const { data, error } = await supabase
        .from('loan_applications')
        .select('*')
        .eq('lender_id', lender.id)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      return data;
    },
  });

  if (error) {
    console.error('Error loading recent applications:', error);
    return (
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Recent Applications</h2>
        <p className="text-red-500">Error loading recent applications</p>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Recent Applications</h2>
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      </Card>
    );
  }

  if (!applications || applications.length === 0) {
    return (
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Recent Applications</h2>
        <p className="text-muted-foreground">No recent applications</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h2 className="text-lg font-semibold mb-4">Recent Applications</h2>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Amount</TableHead>
            <TableHead>Risk Score</TableHead>
            <TableHead>AI Recommendation</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {applications.map((app) => (
            <TableRow key={app.id}>
              <TableCell>${app.amount?.toLocaleString()}</TableCell>
              <TableCell>
                <span className={app.risk_score >= 70 ? "text-green-500" : "text-yellow-500"}>
                  {app.risk_score ? `${app.risk_score}/100` : 'N/A'}
                </span>
              </TableCell>
              <TableCell className="flex items-center gap-2">
                <Brain className="h-4 w-4 text-primary" />
                {app.ai_recommendation || 'Pending'}
              </TableCell>
              <TableCell>
                <span className={`capitalize ${
                  app.status === 'approved' ? 'text-green-500' :
                  app.status === 'rejected' ? 'text-red-500' :
                  'text-yellow-500'
                }`}>
                  {app.status}
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};